import { Button } from '@zep-us/zus';
import { LoadingSpinner } from '@/shared/components';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { type AiBuilderQuestionSetAnswer, canSubmitAiBuilderQuestionSet } from '../store/aiBuilderQuestionSetState';
import { type AiBuilderPendingQuestionSet } from '../store/types';
import { AiBuilderQuestionAnswerControls } from './AiBuilderQuestionAnswerControls';
import { AiBuilderQuestionSetHeader } from './AiBuilderQuestionSetHeader';
export type AiBuilderQuestionSetComposerProps = {
  questionSet: AiBuilderPendingQuestionSet;
  submitting?: boolean;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  onSelectSingleChoice: (questionId: string, optionId: string) => void;
  onUpdateMultipleChoices: (questionId: string, optionIds: readonly string[]) => void;
  onUpdateDirectText: (questionId: string, text: string) => void;
  onConfirmAnswer: (questionId: string) => void;
  onConfirmAndSubmit: (questionId: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onRetrySubmit: () => void;
  onRetryCancel: () => void;
};
export const AiBuilderQuestionSetComposer = ({
  questionSet,
  submitting = false,
  onNavigatePrevious,
  onNavigateNext,
  onSelectSingleChoice,
  onUpdateMultipleChoices,
  onUpdateDirectText,
  onConfirmAnswer,
  onConfirmAndSubmit,
  onSubmit,
  onCancel,
  onRetrySubmit,
  onRetryCancel
}: AiBuilderQuestionSetComposerProps) => {
  const copy = useAiBuilderChatCopy();
  const question = questionSet.questions.find(candidate => candidate.questionId === questionSet.activeQuestionId);
  if (!question) return null;
  const draft = questionSet.questionDrafts[question.questionId];
  const answer = questionSet.questionAnswers[question.questionId];
  const currentValue = draft ?? answer;
  const directMode = currentValue?.mode === 'text' || question.allowDirectInput && question.options.length === 0;
  const confirmable = isConfirmableDraft(question, currentValue);
  const alreadyComplete = canSubmitAiBuilderQuestionSet(questionSet);
  const completesSet = confirmable && questionSet.questions.every(candidate => candidate.questionId === question.questionId || questionSet.questionAnswers[candidate.questionId] !== undefined);
  const showsSubmit = alreadyComplete || completesSet;
  const locked = submitting;
  const error = questionSet.submissionError;
  const retryCancel = error === 'cancel_failed';
  const retrySubmit = error === 'submit_failed';
  const confirmAndContinue = () => {
    if (locked || !confirmable) return;
    if (showsSubmit) {
      onConfirmAndSubmit(question.questionId);
      return;
    }
    onConfirmAnswer(question.questionId);
  };
  const selectSingle = (optionId: string) => {
    if (locked) return;
    onSelectSingleChoice(question.questionId, optionId);
    // The reducer confirms single choice and moves to the next unanswered
    // question. When none remains, submission stays an explicit user action.
  };
  const ctaLabel = showsSubmit ? copy.questionSet.cta.submit : question.selectionMode === 'multiple' && !directMode ? copy.questionSet.cta.completeSelection : copy.questionSet.cta.next;
  return <section className="flex min-w-0 flex-col gap-md rounded-xl bg-default p-md shadow-lg" data-sentry-component="AiBuilderQuestionSetComposer" data-sentry-source-file="AiBuilderQuestionSetComposer.tsx">
      <fieldset disabled={locked} className="min-w-0 border-0 p-0">
        <AiBuilderQuestionSetHeader activeQuestionId={question.questionId} questions={questionSet.questions} onNavigatePrevious={() => !locked && onNavigatePrevious()} onNavigateNext={() => !locked && onNavigateNext()} onCancel={() => !locked && onCancel()} data-sentry-element="AiBuilderQuestionSetHeader" data-sentry-source-file="AiBuilderQuestionSetComposer.tsx" />

        <div className="mt-md min-w-0">
          {directMode ? <AiBuilderQuestionAnswerControls question={question} answerMode="text" directText={currentValue?.mode === 'text' ? currentValue.text : ''} locked={locked} onDirectTextChange={text => onUpdateDirectText(question.questionId, text)} /> : question.selectionMode === 'multiple' ? <AiBuilderQuestionAnswerControls question={question} selectedOptionIds={currentValue?.mode === 'choice' ? currentValue.optionIds : []} locked={locked} onSelectedOptionIdsChange={optionIds => onUpdateMultipleChoices(question.questionId, optionIds)} /> : <AiBuilderQuestionAnswerControls question={question} selectedOptionId={currentValue?.mode === 'choice' ? currentValue.optionIds[0] ?? null : null} locked={locked} onSelectedOptionChange={selectSingle} />}
        </div>
      </fieldset>

      {question.selectionMode !== 'single' || directMode || alreadyComplete ? <div className="flex shrink-0 justify-end">
          <Button type="button" size="sm" className="shrink-0" data-ai-builder-question-set-cta disabled={locked || !alreadyComplete && !confirmable} onClick={alreadyComplete ? onSubmit : confirmAndContinue}>
            {submitting ? <span className="flex items-center justify-center gap-xs" aria-label={copy.questionSet.status.submittingAriaLabel}>
                <LoadingSpinner className="size-md text-current motion-reduce:animate-none" />
                {copy.questionSet.status.submitting}
              </span> : ctaLabel}
          </Button>
        </div> : null}

      {retryCancel || retrySubmit ? <div role="alert" className="flex items-center justify-between gap-sm">
          <p className="text-danger text-body-sm-medium">
            {retryCancel ? copy.questionSet.error.cancelFailed : copy.questionSet.error.submitFailed}
          </p>
          <Button type="button" variant="outlined" intent="alternative" size="sm" disabled={locked} aria-label={retryCancel ? copy.questionSet.error.retryCancelAriaLabel : copy.questionSet.error.retrySubmitAriaLabel} onClick={retryCancel ? onRetryCancel : onRetrySubmit}>
            {copy.questionSet.error.retry}
          </Button>
        </div> : null}
    </section>;
};
const isConfirmableDraft = (question: AiBuilderPendingQuestionSet['questions'][number], value: AiBuilderQuestionSetAnswer | undefined) => {
  if (!value) return false;
  if (value.mode === 'text') {
    return question.allowDirectInput && value.text.trim().length > 0;
  }
  if (value.optionIds.length === 0) return false;
  return question.selectionMode === 'multiple' || value.optionIds.length === 1;
};